'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useState, useRef } from 'react'

const SCROLL_THRESHOLD = 80 // Trigger compact mode after scrolling down 80px
const SHOW_THRESHOLD = 10 // Return to normal mode when scrolling up

export const useHeaderScroll = () => {
	const pathname = usePathname()
	const [isScrollingDown, setIsScrollingDown] = useState(false)
	const [isScrolled, setIsScrolled] = useState(false)

	const lastScrollY = useRef(0)
	const ticking = useRef(false)

	// Sync header scroll state on route change so we don't get stuck in "scrolled" state when Next.js scrolls to top (avoids throttled scroll event being dropped).
	useEffect(() => {
		const y = typeof window !== 'undefined' ? window.scrollY : 0
		lastScrollY.current = y
		if (y < SCROLL_THRESHOLD) {
			setIsScrollingDown(false)
			setIsScrolled(false)
		}
	}, [pathname])

	useEffect(() => {
		const handleScroll = () => {
			if (!ticking.current) {
				window.requestAnimationFrame(() => {
					const currentScrollY = window.scrollY

					// Detect if user has scrolled past threshold
					if (currentScrollY >= SCROLL_THRESHOLD) {
						setIsScrolled(true)
					} else {
						setIsScrolled(false)
					}

					// User scrolling down
					if (currentScrollY > lastScrollY.current && currentScrollY > SCROLL_THRESHOLD) {
						setIsScrollingDown(true)
					}
					// User scrolling up
					else if (currentScrollY < lastScrollY.current - SHOW_THRESHOLD) {
						setIsScrollingDown(false)
					}

					lastScrollY.current = currentScrollY
					ticking.current = false
				})

				ticking.current = true
			}
		}

		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	// Add class to document when header is scrolled
	useEffect(() => {
		if (isScrollingDown) {
			document.documentElement.classList.add('header-mobile--scrolled')
		} else {
			document.documentElement.classList.remove('header-mobile--scrolled')
		}
	}, [isScrollingDown])

	return { isScrollingDown, isScrolled }
}
